import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { CloseOutlined, InfoCircleOutlined, LeftOutlined, MinusOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { appendixReference, benefitGroups, changes, conditions, conditionsIntroduction, generalConditionIds, PAGE_TITLE, sectionTitles, tiers } from './benefits';
import hotelDirectory from './hotel-directory.json';
import './style.scss';

function TierName({ tier }) {
  return <><span className="melia-tier-brand"><strong>Meliá</strong>Rewards</span>{' '}<span>{tier}</span></>;
}

function BrandText({ text }) {
  return text.split('MeliáRewards').map((part, index) => (
    <React.Fragment key={index}>{index > 0 && <><strong>Meliá</strong>Rewards</>}{part}</React.Fragment>
  ));
}

function SourceText({ text, onDirectory }) {
  const [before, after] = text.split(appendixReference);
  return (
    <>
      <BrandText text={before} />
      {after !== undefined && <><button type="button" className="melia-text-link" data-annex-link onClick={onDirectory} aria-label="查看附件二城市与度假酒店">{appendixReference}</button><BrandText text={after} /></>}
    </>
  );
}

function Conditions({ items = conditions, onDirectory }) {
  return items.map((condition) => (
    <div className="melia-condition" key={condition.id} data-condition-id={condition.id}>
      {condition.paragraphs.map((paragraph, index) => (
        <p key={index}>
          {index === 0 && <><span className="melia-condition-letter">{condition.id})</span>{' '}</>}
          <SourceText text={paragraph} onDirectory={onDirectory} />
        </p>
      ))}
    </div>
  ));
}

function BenefitValue({ value }) {
  if (typeof value === 'string') return value;
  return <><span aria-hidden="true">{value ? '•' : ''}</span><span className="melia-sr-only">{value ? '享有' : '未列示'}</span></>;
}

function BenefitDetails({ benefit, onDirectory }) {
  const conditionIds = generalConditionIds.concat(benefit.conditionIds || []);
  return (
    <div className="melia-benefit-details">
      <p className="melia-full-label">{benefit.label}</p>
      <dl className="melia-detail-tiers">
        {benefit.values.map((value, index) => (
          <div key={tiers[index]}>
            <dt><TierName tier={tiers[index]} /></dt>
            <dd>{typeof value === 'string' ? value : (value ? '享有' : '未列示')}</dd>
          </div>
        ))}
      </dl>
      <h3>享用条件</h3>
      <Conditions items={conditions.filter((item) => conditionIds.includes(item.id))} onDirectory={onDirectory} />
    </div>
  );
}

function normalizeSearch(text) {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

function HotelDirectory() {
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const needle = normalizeSearch(query);
  const results = hotelDirectory.types.filter((type) => category === 'all' || category === type.id).map((type) => ({
    ...type,
    regions: type.regions.map((region) => ({
      ...region,
      countries: region.countries.map((country) => ({
        ...country,
        hotels: normalizeSearch(`${type.name} ${type.label} ${region.name} ${country.name}`).includes(needle)
          ? country.hotels : country.hotels.filter((name) => normalizeSearch(name).includes(needle))
      })).filter((country) => country.hotels.length)
    })).filter((region) => region.countries.length)
  })).filter((type) => type.regions.length);
  const resultCount = results.reduce((total, type) => total + type.regions.reduce((count, region) => count + region.countries.reduce((sum, country) => sum + country.hotels.length, 0), 0), 0);

  return (
    <div className="melia-directory">
      <h3>{hotelDirectory.title}</h3>
      <div className="melia-directory-controls">
        <div className="melia-directory-filters" role="group" aria-label="酒店分类">
          {[{ id: 'all', label: '全部' }, ...hotelDirectory.types].map((type) => (
            <button type="button" key={type.id} aria-pressed={category === type.id} onClick={() => setCategory(type.id)}>{type.label}</button>
          ))}
        </div>
        <label className="melia-directory-search">
          <SearchOutlined aria-hidden="true" />
          <input type="search" aria-label="搜索酒店或国家" placeholder="搜索酒店或国家" value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
      </div>
      <p className="melia-result-count" role="status">{resultCount ? `${resultCount} 条结果` : '未找到匹配的酒店。'}</p>
      {results.map((type) => (
        <section key={type.id} aria-label={type.label}>
          <h3>{type.name}</h3>
          {type.regions.map((region, regionIndex) => (
            <section key={regionIndex}>
              <h4>{region.name}</h4>
              {region.countries.map((country, countryIndex) => (
                <div className="melia-directory-country" key={countryIndex}>
                  <h5>{country.name}</h5>
                  <ul>{country.hotels.map((hotel, hotelIndex) => <li key={`${hotel}-${hotelIndex}`}>{hotel}</li>)}</ul>
                </div>
              ))}
            </section>
          ))}
        </section>
      ))}
    </div>
  );
}

export default function MeliaBenefits() {
  const [expanded, setExpanded] = useState(true);
  const [dialog, setDialog] = useState(null);
  const pageRef = useRef(null);
  const dialogScrollRef = useRef(null);
  const returnScrollRef = useRef(0);
  const isOpen = Boolean(dialog);

  useEffect(() => {
    if (!isOpen) return undefined;
    const container = pageRef.current.closest('#container');
    if (!container) return undefined;
    const overflow = container.style.overflowY;
    container.style.overflowY = 'hidden';
    return () => { container.style.overflowY = overflow; };
  }, [isOpen]);

  useLayoutEffect(() => {
    const scroll = dialogScrollRef.current;
    if (!scroll) return;
    if (dialog?.restoring) {
      scroll.querySelector('[data-annex-link]')?.focus({ preventScroll: true });
      scroll.scrollTop = returnScrollRef.current;
    } else {
      scroll.scrollTop = 0;
    }
  }, [dialog]);

  function openDirectory() {
    const returnBenefit = dialog?.kind === 'benefit' ? dialog.benefit : null;
    if (returnBenefit) returnScrollRef.current = dialogScrollRef.current.scrollTop;
    setDialog({ kind: 'directory', returnBenefit });
  }

  return (
    <main className="melia-benefits" ref={pageRef}>
      <h1>{PAGE_TITLE}</h1>
      <section>
        <h2 className="melia-accordion-heading">
          <button type="button" aria-expanded={expanded} aria-controls="melia-plan-content" onClick={() => setExpanded(!expanded)}>
            <span>11.</span><span>计划权益</span>{expanded ? <MinusOutlined aria-hidden="true" /> : <PlusOutlined aria-hidden="true" />}
          </button>
        </h2>
        <div id="melia-plan-content" hidden={!expanded}>
          <h3 className="melia-clause-title"><u>{sectionTitles.benefits}</u></h3>
          <div className="melia-table-scroll" role="region" aria-label="会员等级权益表" tabIndex={0}>
            <table className="melia-comparison" aria-label="MeliáRewards 会员等级权益对照表">
              <colgroup><col className="melia-label-column" />{tiers.map((tier) => <col key={tier} />)}</colgroup>
              <thead>
                <tr><th scope="col"><span className="melia-sr-only">权益</span></th>{tiers.map((tier) => <th scope="col" key={tier}><TierName tier={tier} /></th>)}</tr>
              </thead>
              <tbody>
                {benefitGroups.map((group) => (
                  <React.Fragment key={group.id}>
                    {group.title ? <tr className="melia-group-row"><th scope="row"><strong>{group.title}</strong></th>{tiers.map((tier) => <td key={tier} />)}</tr> : null}
                    {group.rows.map((benefit) => (
                      <tr key={benefit.id}>
                        <th scope="row">
                          <button type="button" className="melia-detail-trigger" aria-label={`查看${benefit.title}权益详情`} aria-haspopup="dialog" onClick={() => setDialog({ kind: 'benefit', benefit })}>
                            <span>{benefit.label}</span> <InfoCircleOutlined aria-hidden="true" />
                          </button>
                        </th>
                        {benefit.values.map((value, index) => <td key={tiers[index]}><BenefitValue value={value} /></td>)}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <section className="melia-conditions">
            <h3 className="melia-clause-title"><u>{sectionTitles.conditions}</u></h3>
            <p>{conditionsIntroduction}</p>
            <Conditions onDirectory={openDirectory} />
          </section>
          <section className="melia-changes">
            <h3 className="melia-clause-title"><u>{sectionTitles.changes}</u></h3>
            <p><SourceText text={changes} /></p>
          </section>
        </div>
      </section>
      <Modal
        open={isOpen}
        title={dialog?.kind === 'directory' ? '城市与度假酒店' : dialog?.benefit.title}
        onCancel={() => setDialog(null)}
        closable={false}
        footer={null}
        centered
        width={420}
        rootClassName={`melia-modal${dialog?.kind === 'directory' ? ' melia-directory-modal' : ''}`}
      >
        <button className="melia-dialog-close" type="button" title="关闭详情" aria-label="关闭详情" onClick={() => setDialog(null)}><CloseOutlined aria-hidden="true" /></button>
        {dialog?.kind === 'directory' && dialog.returnBenefit && <button className="melia-dialog-back" type="button" title="返回权益详情" aria-label="返回权益详情" onClick={() => setDialog({ kind: 'benefit', benefit: dialog.returnBenefit, restoring: true })}><LeftOutlined aria-hidden="true" /></button>}
        {dialog && (
          <div className="melia-dialog-scroll" ref={dialogScrollRef} role="region" aria-label="详情内容" tabIndex={0}>
            {dialog.kind === 'directory' ? <HotelDirectory /> : <BenefitDetails benefit={dialog.benefit} onDirectory={openDirectory} />}
          </div>
        )}
      </Modal>
    </main>
  );
}
